import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { AgentModel } from '../../agent/agent.model';
import { Assert } from '@cotera/utilities';
import { ok } from 'neverthrow';
import { ResourceType } from '@cotera/api';
import type { StoredMessage } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Loading, Text, toast } from '@cotera/client/app/components/ui';
import { ModalPageView } from '@cotera/client/app/components/app';
import { cn } from '../../lib/utils';
import { Suspense, useCallback, useMemo, useState } from 'react';
import type { UIMessage } from 'ai';
import { useSuspenseQuery } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { ErrorBoundary } from 'react-error-boundary';
import { KeyboardNavigableList } from '../../components/ui/keyboard-navigable-list';
import { ChatModel } from '../chat.model';

type ChatClient = Pick<TenantedClient['resource']['chat'], 'listMessages'>;

const describeWhen = (updatedAt: Date) => {
  const now = Date.now();
  const diffMs = now - updatedAt.getTime();

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) {
    return 'just now';
  }
  if (diffMs < hour) {
    const minutes = Math.round(diffMs / minute);
    return `${minutes} min ago`;
  }
  if (diffMs < day) {
    const hours = Math.round(diffMs / hour);
    return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  }

  return updatedAt.toLocaleDateString();
};

type ChatHistoryModalProps = {
  onSelect: (conversationId: string) => Promise<boolean>;
  onClose: () => void;
  agentIdentifier: string | null;
};

const CHAT_LIST_LIMIT = 50;

const useConversations = (_agentIdentifier: string | null) => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  return useSuspenseQuery({
    queryKey: queryKeys.resource.list({
      orgId,
      type: ResourceType.Chat,
      limit: CHAT_LIST_LIMIT,
    }),
    queryFn: async () => {
      const res = await client.resource.list({
        type: ResourceType.Chat,
        limit: CHAT_LIST_LIMIT,
      });
      const out = Assert.assertOk(res);
      if (out.type !== 'chat') {
        return { conversations: [] };
      }
      const conversations = out.items.map((chat) => ({
        id: chat.id,
        actorId: chat.actorId,
        agent: chat.agent,
        agentId: chat.agentId ?? undefined,
        title: chat.title,
        status: chat.status,
        createdAt: chat.createdAt,
        updatedAt: chat.updatedAt,
        lastMessage: (chat.lastMessage ?? null) as StoredMessage | null,
      }));
      return { conversations };
    },
    staleTime: 10,
    refetchOnMount: true,
  });
};

const getLastMessageSnippet = (
  lastMessage: StoredMessage | null
): string | null =>
  (
    lastMessage?.message as {
      content: { text: string };
      parts: { text: string }[];
    }
  )?.content?.text ??
  (lastMessage?.message as { parts: { text: string }[] })?.parts
    ?.map((x) => x.text)
    .join('\n') ??
  null;

const truncateSnippet = (value: string, maxLength = 160): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1)}…`;
};

const SuspensfulChatModal = ({
  onSelect,
  agentIdentifier,
  onClose,
}: {
  onSelect: (conversationId: string) => Promise<boolean>;
  onClose: () => void;
  agentIdentifier: string | null;
}) => {
  return (
    <ModalPageView
      title="Conversation history"
      className="px-4 py-4 flex flex-col gap-4"
    >
      <ErrorBoundary
        fallbackRender={({ error }) => (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <Text.Caption className="text-destructive">
              {error.message}
            </Text.Caption>
          </div>
        )}
      >
        <Suspense
          fallback={
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <Loading.Dots />
              <Text.Caption className="text-muted-foreground">
                Loading conversations…
              </Text.Caption>
            </div>
          }
        >
          <ChatHistoryModal
            agentIdentifier={agentIdentifier}
            onSelect={onSelect}
            onClose={onClose}
          />
        </Suspense>
      </ErrorBoundary>
    </ModalPageView>
  );
};

const ChatHistoryModal: React.FC<ChatHistoryModalProps> = ({
  onSelect,
  onClose,
  agentIdentifier,
}) => {
  const { data: conversations } = useConversations(agentIdentifier);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const items = useMemo(() => {
    const list = conversations.conversations;
    if (agentIdentifier === null) {
      return list;
    }
    return list.filter((c) => c.agent === agentIdentifier);
  }, [conversations.conversations, agentIdentifier]);

  const emptyState = useMemo(() => {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8">
        <Text.Caption className="text-muted-foreground text-center">
          No previous conversations found.
        </Text.Caption>
      </div>
    );
  }, []);

  const handleSelect = useCallback(
    async (conversationId: string) => {
      const wasSuccessful = await onSelect(conversationId);
      if (wasSuccessful) {
        onClose();
      }
    },
    [onClose, onSelect]
  );

  if (items.length === 0) {
    return emptyState;
  }

  return (
    <KeyboardNavigableList
      itemCount={items.length}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
      onSelectItem={async (index) => {
        const conversation = items[index];
        if (!conversation) {
          return;
        }
        await handleSelect(conversation.id);
      }}
      onEscape={onClose}
      containerClassName="outline-none"
    >
      {({ listRef, getItemProps, activeDescendantId }) => (
        <div
          ref={listRef}
          className="flex flex-col gap-1 px-2 py-1 overflow-y-auto max-h-[500px]"
          role="listbox"
          tabIndex={0}
          aria-activedescendant={activeDescendantId}
        >
          {items.map((conversation, index) => {
            const snippet = getLastMessageSnippet(conversation.lastMessage);
            const displaySnippet = snippet
              ? truncateSnippet(snippet, 180)
              : null;
            const isSelected = index === selectedIndex;
            const { className: navigableClassName, ...navigableProps } =
              getItemProps(index, { id: `chat-history-item-${index}` });

            return (
              <div
                key={conversation.id}
                {...navigableProps}
                className={cn(
                  'text-sm text-standard-text w-full rounded-md cursor-pointer transition-colors',
                  isSelected
                    ? 'bg-zinc-50 ring-1 ring-gap-2 ring-zinc-300 z-20 relative'
                    : 'hover:bg-zinc-50 focus:bg-zinc-50 active:bg-zinc-50 data-focus:bg-zinc-50 z-0',
                  navigableClassName
                )}
              >
                <div className="flex items-start justify-between gap-4 py-3 px-4">
                  <div className="flex flex-col gap-2 min-w-0">
                    <span className="flex items-center gap-2 min-w-0">
                      <Text.P className="font-medium truncate">
                        {conversation.title}
                      </Text.P>
                    </span>
                    {displaySnippet && (
                      <Text.Caption className="text-muted-foreground truncate">
                        {displaySnippet}
                      </Text.Caption>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 flex-shrink-0">
                    <Text.Caption className="text-muted-foreground whitespace-nowrap">
                      {describeWhen(new Date(conversation.updatedAt))}
                    </Text.Caption>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </KeyboardNavigableList>
  );
};

export class OpenChatHistoryAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Conversation History';
  inputSchema = z.object({});
  group = 'Chat';
  icon = 'history';

  constructor(
    private readonly userId: string,
    private readonly client: ChatClient,
    private readonly modalManager: {
      push: (props: { view: () => React.ReactNode }) => void;
      close: () => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AgentModel);
  }

  async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agentModel = context.targetedModelOfType(AgentModel);
    Assert.assert(agentModel !== null, 'Agent model not found');

    const handleSelect = async (conversationId: string): Promise<boolean> => {
      const result = await this.client.listMessages({
        conversationId,
        userId: this.userId,
      });
      if (result.isErr()) {
        toast.error('Unable to load conversation messages');
        return false;
      }

      const messages = result.value.messages.map((x) => x.message as UIMessage);

      agentModel.chatId.set(conversationId);

      //a bit gross, but we need to wait for the chat model to be created
      //as it will be blown away by the new chat id
      setTimeout(() => {
        const chatModel = context.targetedModelOfType(ChatModel);
        Assert.assert(chatModel !== null, 'Chat model not found');
        agentModel.hasMessages.set(true);
        chatModel.chatController.setMessages(messages);
      }, 100);

      return true;
    };

    this.modalManager.push({
      view: () => (
        <SuspensfulChatModal
          agentIdentifier={
            agentModel.agentIdentity.snapshot()?.toString() ?? null
          }
          onSelect={handleSelect}
          onClose={() => {
            this.modalManager.close();
          }}
        />
      ),
    });

    return ok({
      next: true,
    });
  }
}
